import { apiClient } from './client'
import type { UploadLink } from '@/types/domain'

const http = () => apiClient.axios

/**
 * Shareable upload links: a token a phone can open, with no session, to
 * photograph a form straight into one template. The cockpit shows one as a
 * QR code.
 */

/**
 * Overrides the host in the link the gateway builds.
 *
 * The gateway composes `url` from its own `GATEWAY_PUBLIC_UPLOAD_BASE_URL`,
 * which in local development still names the production capture site — so a
 * QR code scanned off a dev screen would upload a real form into the live
 * system. Setting `VITE_PUBLIC_UPLOAD_BASE_URL` points the link at whatever
 * this build's capture site is instead. Unset, the gateway's own URL is used
 * unchanged, which is what production wants.
 */
const PUBLIC_UPLOAD_BASE_URL = import.meta.env.VITE_PUBLIC_UPLOAD_BASE_URL ?? ''

/** The address a phone opens for `token`, honouring the override. */
export function publicUploadUrl(token: string, gatewayUrl?: string): string {
  if (PUBLIC_UPLOAD_BASE_URL) {
    return `${PUBLIC_UPLOAD_BASE_URL.replace(/\/+$/, '')}/upload/${token}`
  }
  return gatewayUrl ?? ''
}

function normalize(raw: UploadLink): UploadLink {
  return { ...raw, url: publicUploadUrl(raw.token, raw.url) }
}

/** Neither revoked nor past its expiry — the only kind the public endpoints
 *  answer for. The gateway does not mark this on the row, so the list has to
 *  work it out to grey out the dead ones. */
export function isUploadLinkActive(link: UploadLink, now: Date = new Date()): boolean {
  if (link.revokedAt) return false
  if (link.expiresAt && new Date(link.expiresAt).getTime() <= now.getTime()) return false
  return true
}

export const uploadLinksApi = {
  async list(templateId: string): Promise<UploadLink[]> {
    const { data } = await http().get<{ links?: UploadLink[] }>(
      `/v1/templates/${templateId}/upload-links`,
    )
    return (data.links ?? []).map(normalize)
  },

  /** `expiresInDays` omitted (or not positive) creates a link that never
   *  expires; it can still be revoked. */
  async create(
    templateId: string,
    options: { expiresInDays?: number } = {},
  ): Promise<UploadLink> {
    const body =
      options.expiresInDays != null ? { expiresInDays: options.expiresInDays } : undefined
    const { data } = await http().post<UploadLink>(
      `/v1/templates/${templateId}/upload-links`,
      body,
    )
    return normalize(data)
  },

  /** Revoking is permanent — the row stays, stamped `revokedAt`, so a link
   *  handed out on paper can never be made to work again. */
  async revoke(templateId: string, token: string): Promise<void> {
    await http().delete(`/v1/templates/${templateId}/upload-links/${token}`)
  },
}
